import * as XLSX from 'xlsx';
import type { Task, User, Board, Team, Label, Sprint } from '../types';

const statusLabels: Record<string, string> = {
  backlog: 'Backlog',
  todo: 'A Fazer',
  in_progress: 'Em Progresso',
  in_review: 'Em Revisão',
  done: 'Concluído',
};

const priorityLabels: Record<string, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
  critical: 'Crítica',
};

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

function formatExportDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function findUserName(userId: string | null, users: User[]): string {
  if (!userId) return '';
  const user = users.find((u) => u.id === userId);
  return user ? user.name : '';
}

function findTeamName(boardId: string, boards: Board[], teams: Team[]): string {
  const board = boards.find((b) => b.id === boardId);
  if (!board) return '';
  const team = teams.find((t) => t.id === board.teamId);
  return team ? team.name : '';
}

function findBoardName(boardId: string, boards: Board[]): string {
  const board = boards.find((b) => b.id === boardId);
  return board ? board.name : '';
}

function findSprintName(taskId: string, sprints: Sprint[]): string {
  const sprint = sprints.find((s) => s.taskIds.includes(taskId));
  return sprint ? sprint.name : '';
}

interface ExportParams {
  tasks: Task[];
  users: User[];
  boards: Board[];
  teams: Team[];
  labels: Label[];
  sprints: Sprint[];
  fileName: string;
  includeSubtasks: boolean;
}

function buildTaskRows(params: ExportParams): Record<string, string | number>[] {
  const { tasks, users, boards, teams, labels, sprints, includeSubtasks } = params;
  const rows: Record<string, string | number>[] = [];

  for (const task of tasks) {
    const assigneeNames = task.assigneeIds
      .map((id) => findUserName(id, users))
      .filter(Boolean)
      .join(', ');

    const labelNames = task.labelIds
      .map((id) => {
        const label = labels.find((l) => l.id === id);
        return label ? label.name : '';
      })
      .filter(Boolean)
      .join(', ');

    rows.push({
      'Tipo': 'Tarefa',
      'Tarefa Pai': '',
      'Nome da Tarefa': task.title,
      'Descrição': stripHtml(task.description),
      'Status': statusLabels[task.status] || task.status,
      'Prioridade': priorityLabels[task.priority] || task.priority,
      'Responsável': assigneeNames,
      'Equipe': findTeamName(task.boardId, boards, teams),
      'Board': findBoardName(task.boardId, boards),
      'Story Points': task.storyPoints,
      'Etiquetas': labelNames,
      'Data de Início': formatExportDate(task.createdAt),
      'Data Limite': formatExportDate(task.dueDate),
      'Horas': '',
      'Sprint': findSprintName(task.id, sprints),
    });

    if (includeSubtasks && task.subtasks.length > 0) {
      for (const subtask of task.subtasks) {
        rows.push({
          'Tipo': 'Subtarefa',
          'Tarefa Pai': task.title,
          'Nome da Tarefa': subtask.title,
          'Descrição': '',
          'Status': subtask.completed ? 'Concluída' : 'Pendente',
          'Prioridade': priorityLabels[task.priority] || task.priority,
          'Responsável': findUserName(subtask.assigneeId, users),
          'Equipe': findTeamName(task.boardId, boards, teams),
          'Board': findBoardName(task.boardId, boards),
          'Story Points': '',
          'Etiquetas': '',
          'Data de Início': formatExportDate(subtask.startDate),
          'Data Limite': formatExportDate(subtask.endDate),
          'Horas': subtask.hours,
          'Sprint': findSprintName(task.id, sprints),
        });
      }
    }
  }

  return rows;
}

export function exportTasksToXlsx(params: ExportParams): void {
  const rows = buildTaskRows(params);
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Tarefas');
  XLSX.writeFile(workbook, params.fileName);
}

interface BoardExportParams extends ExportParams {
  boardId: string;
}

export function exportBoardToXlsx(params: BoardExportParams): void {
  const filteredTasks = params.tasks.filter((t) => t.boardId === params.boardId);
  const rows = buildTaskRows({ ...params, tasks: filteredTasks });
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  const boardName = findBoardName(params.boardId, params.boards) || 'Board';
  XLSX.utils.book_append_sheet(workbook, worksheet, boardName.slice(0, 31));
  XLSX.writeFile(workbook, params.fileName);
}

export function exportAllBoardsToXlsx(params: ExportParams): void {
  const workbook = XLSX.utils.book_new();

  for (const board of params.boards) {
    const filteredTasks = params.tasks.filter((t) => t.boardId === board.id);
    const rows = buildTaskRows({ ...params, tasks: filteredTasks });
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, board.name.slice(0, 31));
  }

  XLSX.writeFile(workbook, params.fileName);
}
